import resolveBreadcrumbLink from '@/lib/resolveBreadcrumbLink'
import { getLocaleFromPath, getSiteUrl, localizePath } from '@/lib/locales'
import { pickSignificantImageUrl } from '@/lib/ogImage'
import {
  CLINIC,
  CLINIC_ID,
  MEDICAL_SPECIALTIES,
  MEDICAL_SPECIALTY_ALIASES,
  PROCEDURE_TYPES,
  SITE_ORIGIN,
} from './constants'
import {
  compact,
  getBlockContent,
  pickImageUrl,
  stripHtml,
  toAbsoluteUrl,
  truncateText,
} from './utils'

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const OPENING_HOURS = WEEKDAYS.map((dayOfWeek) => ({
  '@type': 'OpeningHoursSpecification',
  dayOfWeek,
  opens: '08:00',
  closes: '20:00',
}))

const MEDICAL_SPECIALTIES_BY_LENGTH = [...MEDICAL_SPECIALTIES].sort((a, b) => b.length - a.length)

function toSpecialtyUrl(name) {
  return `https://schema.org/${name}`
}

function resolveMedicalSpecialty(direction) {
  const haystack = `${direction?.slug || ''} ${direction?.title || ''}`.toLowerCase()
  if (!haystack.trim()) {
    return null
  }

  const alias = MEDICAL_SPECIALTY_ALIASES.find(({ keywords }) =>
    keywords.some((keyword) => haystack.includes(keyword))
  )
  if (alias) {
    return toSpecialtyUrl(alias.specialty)
  }

  const compactHaystack = haystack.replace(/[^a-zа-яё0-9]+/gi, '')
  const specialty = MEDICAL_SPECIALTIES_BY_LENGTH.find((name) =>
    compactHaystack.includes(name.toLowerCase())
  )

  return specialty ? toSpecialtyUrl(specialty) : null
}

function buildMedicalSpecialties(directions = []) {
  const specialties = directions.map(resolveMedicalSpecialty).filter(Boolean)

  return [...new Set(specialties)]
}

function buildPhysicianSpecialties(specialist = {}) {
  const fromDirections = buildMedicalSpecialties(
    (specialist.services || []).map((service) => service?.direction).filter(Boolean)
  )
  if (fromDirections.length) {
    return fromDirections
  }

  const position = stripHtml(specialist.position || '')
  if (!position) {
    return []
  }

  const fromPosition = resolveMedicalSpecialty({ title: position, slug: position })
  return fromPosition ? [fromPosition] : [position]
}

export function buildMedicalClinic({ directions = [] } = {}) {
  const medicalSpecialty = buildMedicalSpecialties(directions)

  return compact({
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    '@id': CLINIC_ID,
    name: CLINIC.name,
    url: CLINIC.url,
    logo: CLINIC.logo,
    image: CLINIC.image,
    telephone: CLINIC.telephone,
    email: CLINIC.email,
    priceRange: CLINIC.priceRange,
    isAcceptingNewPatients: CLINIC.isAcceptingNewPatients,
    foundingDate: CLINIC.foundingDate,
    sameAs: CLINIC.sameAs,
    hasMap: CLINIC.hasMap,
    medicalSpecialty: medicalSpecialty.length ? medicalSpecialty : undefined,
    identifier: {
      '@type': 'PropertyValue',
      name: 'DHA License',
      value: CLINIC.dhaLicense,
    },
    address: {
      '@type': 'PostalAddress',
      ...CLINIC.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: CLINIC.geo.latitude,
      longitude: CLINIC.geo.longitude,
    },
    openingHoursSpecification: OPENING_HOURS,
  })
}

export function buildBreadcrumbList(items = [], { host } = {}) {
  if (!items || items.length < 2) {
    return null
  }

  const siteUrl = getSiteUrl(host)

  // Same items and same href/as resolution as the visible Breadcrumbs component.
  const list = items.map((item, index, arr) => {
    const isLast = index === arr.length - 1
    const { as } = resolveBreadcrumbLink(item)

    const entry = {
      '@type': 'ListItem',
      position: index + 1,
      name: stripHtml(item?.title || ''),
    }

    // Google: the last crumb must not have `item`.
    if (!isLast) {
      entry.item = toAbsoluteUrl(as, siteUrl)
    }

    return entry
  })

  return compact({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list,
  })
}

export function buildFaqPage(questions = []) {
  const valid = (questions || []).filter((item) => item && item.question && item.answer)
  if (!valid.length) {
    return null
  }

  return compact({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: valid.map((item) => ({
      '@type': 'Question',
      name: stripHtml(item.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(item.answer),
      },
    })),
  })
}

function toSchemaDate(value) {
  if (!value) {
    return undefined
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toISOString().slice(0, 10)
}

function resolveProcedureType(service = {}) {
  const type = service.procedure_type || service.direction?.procedure_type
  return PROCEDURE_TYPES.has(type) ? type : 'MedicalProcedure'
}

function getServiceUrl(service = {}, { siteUrl, locale, pageUrl } = {}) {
  return (
    pageUrl ||
    toAbsoluteUrl(
      localizePath(`/${service?.direction?.slug || ''}/${service?.slug || ''}`.replace(/\/+/g, '/'), locale),
      siteUrl
    )
  )
}

function getPhysicianSlug(value) {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    return value.trim() || null
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const slug = getPhysicianSlug(item)
      if (slug) {
        return slug
      }
    }
    return null
  }

  return getPhysicianSlug(value.slug)
}

function getRelatedPhysicianSlug(service = {}) {
  return (
    getPhysicianSlug(service.author) ||
    getPhysicianSlug(service.specialist) ||
    getPhysicianSlug(service.physician) ||
    getPhysicianSlug(service.reviewed_by)
  )
}

function toPhysicianRef(slug, { siteUrl, locale } = {}) {
  if (!slug) {
    return undefined
  }

  const url = toAbsoluteUrl(localizePath(`/specialists/${slug}`, locale), siteUrl)
  return { '@id': `${url}#physician` }
}

function toOfferPrice(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined
  }

  if (typeof value === 'string') {
    const price = value.trim()
    return price || undefined
  }

  return undefined
}

function buildOffer(price, url) {
  const offerPrice = toOfferPrice(price)
  if (!offerPrice) {
    return undefined
  }

  return {
    '@type': 'Offer',
    priceCurrency: 'AED',
    price: offerPrice,
    availability: 'https://schema.org/InStock',
    url,
  }
}

function buildMedicalProcedure(service = {}, { siteUrl, locale, pageUrl } = {}) {
  const url = getServiceUrl(service, { siteUrl, locale, pageUrl })
  const firstBlockContent = getBlockContent(service.blocks?.[0])

  return compact({
    '@type': resolveProcedureType(service),
    '@id': `${url}#procedure`,
    name: service.title,
    howPerformed: firstBlockContent ? truncateText(firstBlockContent, 500) : undefined,
    preparation: service.preparation ? stripHtml(service.preparation) : undefined,
    followup: service.followup ? stripHtml(service.followup) : undefined,
    lastReviewed: toSchemaDate(service.last_reviewed),
    offers: buildOffer(service.price, url),
    provider: { '@id': CLINIC_ID },
  })
}

export function buildServiceSchemas(service = {}, { host, asPath, pageUrl } = {}) {
  const siteUrl = getSiteUrl(host)
  const locale = getLocaleFromPath(asPath || pageUrl || '/')
  const url = getServiceUrl(service, { siteUrl, locale, pageUrl })
  const description = service?.meta?.description || service.description || 'ELEMENTAL Day Surgical Center'
  const lastReviewed = toSchemaDate(service.last_reviewed)
  const bannerUrl = pickSignificantImageUrl(service.banner)
  const physician = toPhysicianRef(getRelatedPhysicianSlug(service), { siteUrl, locale })

  return compact({
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${url}#webpage`,
    name: service.title,
    description: stripHtml(description),
    url,
    image: bannerUrl ? toAbsoluteUrl(bannerUrl, siteUrl) : undefined,
    inLanguage: locale === 'ar' ? 'ar' : 'en',
    isPartOf: { '@id': CLINIC_ID },
    about: buildMedicalProcedure(service, { siteUrl, locale, pageUrl }),
    lastReviewed,
    reviewedBy: physician,
    author: physician,
    provider: { '@id': CLINIC_ID },
  })
}

export function buildPhysicianSchema(specialist = {}, { host, asPath, pageUrl } = {}) {
  const siteUrl = getSiteUrl(host)
  const locale = getLocaleFromPath(asPath || pageUrl || '/')
  const path = `/specialists/${specialist.slug || ''}`
  const url = pageUrl || toAbsoluteUrl(localizePath(path, locale), siteUrl)
  const name = [specialist.last_name, specialist.first_name, specialist.patronymic]
    .filter(Boolean)
    .join(' ')
    .trim()

  const specialties = buildPhysicianSpecialties(specialist)

  const availableService = (specialist.services || [])
    .map((service) => buildMedicalProcedure(service, { siteUrl, locale }))
    .filter(Boolean)

  const image = pickImageUrl(specialist.image) || pickImageUrl(specialist.preview)

  return compact({
    '@context': 'https://schema.org',
    '@type': 'Physician',
    '@id': `${url}#physician`,
    name,
    description: specialist.qualification || undefined,
    url,
    image: image ? toAbsoluteUrl(image, siteUrl) : undefined,
    address: {
      '@type': 'PostalAddress',
      ...CLINIC.address,
    },
    parentOrganization: { '@id': CLINIC_ID },
    medicalSpecialty: specialties.length ? specialties : undefined,
    availableService: availableService.length ? availableService : undefined,
    identifier: specialist.license_number
      ? {
          '@type': 'PropertyValue',
          name: 'DHA License',
          value: specialist.license_number,
        }
      : undefined,
  })
}

export { CLINIC_ID, SITE_ORIGIN }
